import {
  Alert,
  Button,
  Card,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { profileApi } from '../../../../api/profile'
import { useWebPushSubscriptionContext } from '../../../../components/Notifications/WebPushSubscriptionProvider'
import { useIsPwa } from '../../../../hooks/useIsPwa'
import {
  buildUserNotificationsMetaPatch,
  getDefaultUserNotificationsMeta,
  normalizeUserNotificationsMeta,
  type NotificationGroupKey,
} from '../../../../types/entitys/userSettings'
import { extractApiErrorMessage } from '../../../../utils/api'
import { SettingsSectionShell } from './SettingsSectionShell'

function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') {
    return false
  }
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

function BrowserPushSection() {
  const { state, disablePush } = useWebPushSubscriptionContext()
  const isPwa = useIsPwa()
  const showIosHint = isIosDevice() && !isPwa

  const handleDisablePush = () => {
    void disablePush().then(() => {
      toast.info('Push отключены')
    })
  }

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Typography variant="black-14-medium">Push в браузере / PWA</Typography>

        {state === 'loading' && <CircularProgress size={20} />}

        {state === 'unsupported' && (
          <Typography variant="black-14-normal" color="text.secondary">
            Push недоступен в этом браузере.
          </Typography>
        )}

        {state === 'default' && (
          <Stack spacing={0.5}>
            <Typography variant="black-14-normal">Push не включены</Typography>
            <Typography variant="black-14-normal" color="text.secondary">
              Включить можно в разделе События
            </Typography>
          </Stack>
        )}

        {state === 'denied' && (
          <Stack spacing={1}>
            <Alert severity="warning">
              Разрешите уведомления в настройках браузера или Chrome.
            </Alert>
            {showIosHint && (
              <Typography variant="black-14-normal" color="text.secondary">
                На iPhone: добавьте приложение на экран «Домой».
              </Typography>
            )}
          </Stack>
        )}

        {state === 'enabled' && (
          <FormControlLabel
            control={<Switch checked onChange={handleDisablePush} />}
            label="Push-уведомления включены"
          />
        )}
      </Stack>
    </Card>
  )
}

export const NotificationsSection = () => {
  const { data: profile } = profileApi.useGetProfileQuery()
  const [updateUserProfile, updateState] = profileApi.useUpdateUserProfileMutation()
  const [groups, setGroups] = useState(getDefaultUserNotificationsMeta().groups)

  useEffect(() => {
    setGroups(normalizeUserNotificationsMeta(profile?.meta).groups)
  }, [profile?.meta])

  const hasChanges = useMemo(() => {
    const initial = normalizeUserNotificationsMeta(profile?.meta).groups
    return (
      groups.forum.enabled !== initial.forum.enabled ||
      groups.projects.enabled !== initial.projects.enabled ||
      groups.teams.enabled !== initial.teams.enabled ||
      groups.socialOrders.enabled !== initial.socialOrders.enabled
    )
  }, [groups, profile?.meta])

  const toggleGroup = (group: NotificationGroupKey, enabled: boolean) => {
    if (group === 'system') {
      return
    }

    setGroups((prev) => ({
      ...prev,
      [group]: { enabled },
    }))
  }

  const handleSave = async () => {
    if (!profile?.id) return

    try {
      const current = normalizeUserNotificationsMeta(profile.meta)
      await updateUserProfile({
        userId: profile.id,
        body: {
          meta: buildUserNotificationsMetaPatch(profile.meta, {
            ...current,
            groups: {
              ...groups,
              system: { enabled: true },
            },
          }),
        },
      }).unwrap()
      toast.success('Настройки уведомлений сохранены')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось сохранить настройки уведомлений')
    }
  }

  return (
    <SettingsSectionShell
      title="Уведомления"
      description="Здесь вы управляете тем, какие события можно доставлять вам через in-app ленту, email и push, а также текущим состоянием браузерного push-канала."
    >
      <BrowserPushSection />

      <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Typography variant="black-14-medium">Категории уведомлений</Typography>
          <Typography variant="black-14-light" color="text.secondary">
            Эти настройки управляют тем, какие события попадут в ленту уведомлений, email и push. Системные
            уведомления остаются включёнными всегда.
          </Typography>

          {!profile?.id ? (
            <Stack direction="row" gap={1.5} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="black-14-light" color="text.secondary">
                Загружаем настройки...
              </Typography>
            </Stack>
          ) : (
            <>
              <FormControlLabel
                control={<Switch checked={groups.forum.enabled} onChange={(e) => toggleGroup('forum', e.target.checked)} />}
                label="Форум: комментарии и ответы"
              />
              <FormControlLabel
                control={<Switch checked={groups.projects.enabled} onChange={(e) => toggleGroup('projects', e.target.checked)} />}
                label="Проекты"
              />
              <FormControlLabel
                control={<Switch checked={groups.teams.enabled} onChange={(e) => toggleGroup('teams', e.target.checked)} />}
                label="Команды"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={groups.socialOrders.enabled}
                    onChange={(e) => toggleGroup('socialOrders', e.target.checked)}
                  />
                }
                label="Соц-заказы"
              />
              <FormControlLabel
                control={<Switch checked disabled />}
                label="Системные уведомления"
              />
              <Stack direction="row">
                <Button variant="follow" onClick={() => void handleSave()} disabled={!hasChanges || updateState.isLoading}>
                  {updateState.isLoading ? 'Сохраняем...' : 'Сохранить'}
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </Card>
    </SettingsSectionShell>
  )
}
